// =====================================================================
// Chamados — o guia GPS-TI na COR DO MÓDULO.
//
// ---------------------------------------------------------------------
// O PROBLEMA
// ---------------------------------------------------------------------
// O guia nasceu no **vermelho institucional** (`--red:#BC0005`) com o **amarelo
// das ações** (`--signal:#FAB915`). Dentro do portal ele deixou de ser um site à
// parte: é a aba *Manual do TI* do módulo **Chamados**, que é **roxo `#6b21a8`**
// — abrir a aba trocava a cor da tela inteira e a pessoa perdia a referência de
// onde estava.
//
// ---------------------------------------------------------------------
// A SOLUÇÃO: sobrescrever o ESTILO, não o guia
// ---------------------------------------------------------------------
// A paleta do guia é **centralizada em variáveis CSS no `:root`** — medido em
// 2026-09-01: 4 definições literais e ~116 usos por `var(…)`. Injetar um
// `<style>` no `srcDoc` que redeclara essas variáveis vira o guia inteiro: topo,
// títulos, botões, painéis, contornos de foco.
//
// ⚠️⚠️ A POSIÇÃO É O PONTO: o bloco entra **antes de `</head>`**, ou seja
//    DEPOIS do `<style>` do guia. Variável de `:root` não tem especificidade
//    que decida — quem vem por último vence. Injetado no começo do `<head>`
//    (onde entra o shim de `localStorage`), este bloco perderia e não faria
//    nada visível, que é o modo de falha difícil de perceber.
//
// ⚠️⚠️ POR QUE NO PORTAL E NÃO NO GERADOR — mesma razão do shim de storage
//    (`chamados-guia-storage.js`): por transformação seria mais uma âncora
//    envelhecendo a cada revisão do Sebastião, e exigiria **regerar** o guia
//    (que depende do arquivo dele, fora do repo) só para trocar cor. Assim,
//    revisão nova dele já chega pintada.
//
// ⚠️ FALHA ABERTA: HTML sem `</head>` volta intacto. Guia na cor errada é
//    infinitamente melhor que guia que não abre.
// =====================================================================

// ---------------------------------------------------------------------
// A PALETA
// ---------------------------------------------------------------------
// O roxo é o do módulo (mesmos valores do hub `Chamados.js`); os cinzas são o
// slate que o portal inteiro usa — os mesmos tons que já aparecem no guia em
// `--rule:#E2E8F0` e `--muted:#64748B`.
// ⚠️⚠️ AQUI NÃO ENTRA `var(--ch-cor)`, E ISSO JÁ FOI QUEBRADO UMA VEZ. Entre
//    17/09 e 21/09/2026 estas duas constantes apontaram para a variável do
//    portal, e o guia ficou SEM COR NENHUMA: ele roda num `<iframe srcDoc>` com
//    `sandbox` sem `allow-same-origin` (`ChamadosManual.js`), que é um DOCUMENTO
//    SEPARADO em origem opaca — variável CSS não atravessa essa fronteira.
//    `var(--ch-cor)` resolvia para nada, `--red` ficava vazio, e todo
//    `color:var(--red)` do guia caía para herança: os campos da *Triagem rápida*
//    sumiram (texto sem cor, não layout quebrado).
//    O matiz por fila continua existindo — mas ele chega aqui **já resolvido em
//    hex**, por `resolverCoresDoModulo()`. Estes valores são só o PADRÃO (o roxo
//    do `:root`), usados quando não há DOM para ler.
const ROXO_PADRAO = '#6b21a8';         // era --red        (#BC0005)
const ROXO_ESCURO_PADRAO = '#581c87';  // era --red-dark   (#8E0004)
const ROXO_CLARO = '#faf5ff';   // fundo pálido roxo, para o que era creme
const CINZA = '#cbd5e1';        // era --signal     (#FAB915) — o acento
const CINZA_CLARO = '#e2e8f0';  // era #FFC72E / #FFD874 — o "mais claro" do par
const CINZA_PALIDO = '#f1f5f9'; // era #FEF6E0 / #FFF3D1 — fundo de painel
const CINZA_TEXTO = '#334155';  // era #5A4408 — o texto escuro sobre os dois acima
const CINZA_FORTE = '#94a3b8';  // só o realce da busca (ver abaixo)
const BRILHO = '148,163,184';   // o RGB do CINZA_FORTE, para os halos de foco

/**
 * O `<style>` que repinta o guia.
 *
 * ⚠️⚠️ TROCAR A VARIÁVEL NÃO BASTA, e é isso que faz este bloco ser longo. O
 *    amarelo tem **literais colados nele** em 59 regras do guia — hover, fundos
 *    de painel, texto escuro por cima — e nenhum deles é variável. Virar só o
 *    `--signal` deixaria **texto marrom (`#5A4408`) sobre cinza** e **painéis
 *    creme (`#FEF6E0`) com borda cinza** espalhados pelo guia, em cantos que
 *    ninguém revisaria. Cada grupo abaixo é um desses literais.
 *
 * ⚠️ `--alert` (o vermelho de alerta) FICA VERMELHO de propósito: é o que marca
 *    a escala vencida. Antes ele era quase a cor da marca e se perdia no meio;
 *    com o resto roxo e cinza, virou a única coisa vermelha da tela — ficou
 *    mais legível, não menos.
 *
 * ⚠️ O `<meta name="theme-color" content="#BC0005">` do guia fica como está:
 *    dentro de um iframe ele não pinta nada (quem manda na barra do navegador é
 *    a página que hospeda). E os hex vermelhos das **legendas de cabo** das
 *    fotos (`#DD0308` = "cabo de dados da impressora") são **dado** — descrevem
 *    o cabo de verdade e não podem ser repintados.
 */
export const ID_TEMA = 'portal-tema';

/**
 * Lê do PORTAL a cor que a fila está usando agora, **já resolvida em hex**.
 *
 * @returns {{cor: string, corEscura: string}} sempre preenchido
 *
 * ⚠️⚠️ É ESTA FUNÇÃO QUE MANTÉM O `index.css` COMO FONTE ÚNICA. O matiz muda com
 *    a fila (roxo no TI, índigo na Manutenção) e mora em `.modulo-chamados` /
 *    `.modulo-chamados--manutencao`. Copiar os hex para cá criaria a segunda
 *    verdade que a mudança de 17/09 tentava eliminar — ela só errou o COMO.
 *
 * ⚠️ Lê do `.modulo-chamados` e NÃO do `document.documentElement`: o índigo da
 *    Manutenção é declarado na classe do hub, não no `:root`. Lendo da raiz, a
 *    fila de Manutenção receberia o roxo do TI — sem erro nenhum.
 *
 * ⚠️ FALHA ABERTA, no mesmo princípio do resto do arquivo: sem DOM (teste), sem
 *    o hub montado ou com o `getComputedStyle` lançando, devolve o padrão. Guia
 *    na cor errada é infinitamente melhor que guia que não abre.
 */
export function resolverCoresDoModulo() {
  const padrao = { cor: ROXO_PADRAO, corEscura: ROXO_ESCURO_PADRAO };
  try {
    if (typeof document === 'undefined' || !document.querySelector) return padrao;
    const alvo = document.querySelector('.modulo-chamados') || document.documentElement;
    if (!alvo) return padrao;
    const estilo = window.getComputedStyle(alvo);
    // ⚠️ `trim()` não é zelo: o valor de uma variável CSS volta COM o espaço que
    //    separa os dois-pontos do hex no arquivo (`--ch-cor: #6b21a8`), e um
    //    `#6b21a8` com espaço na frente é declaração inválida dentro do guia.
    const cor = String(estilo.getPropertyValue('--ch-cor') || '').trim();
    const corEscura = String(estilo.getPropertyValue('--ch-cor-escura') || '').trim();
    return { cor: cor || padrao.cor, corEscura: corEscura || padrao.corEscura };
  } catch {
    return padrao;
  }
}

/**
 * @param {{cor?: string, corEscura?: string}} [cores] o matiz da fila, em HEX já
 *        resolvido (ver `resolverCoresDoModulo`). Omitido = o roxo padrão.
 */
export function montarTemaDoModulo(cores) {
  const ROXO = (cores && cores.cor) || ROXO_PADRAO;
  const ROXO_ESCURO = (cores && cores.corEscura) || ROXO_ESCURO_PADRAO;
  // ⚠️ Mesmo motivo do `ID_SHIM`: o backend remove este bloco antes de gravar a
  //    revisão que o Sebastião salva, senão cada gravação empilha outra folha.
  return `<style id="${ID_TEMA}">
  /* ================================================================
     Portal › Chamados: o guia herda a cor do módulo.
     Fonte e razão de cada linha: chamados-guia-tema.js
     ================================================================ */

  :root{ --red:${ROXO}; --red-dark:${ROXO_ESCURO}; --signal:${CINZA}; }

  /* O par mais claro do acento — era o amarelo vivo do hover (#FFC72E) e o
     amarelo pálido escrito sobre o roxo (#FFD874). */
  .entendido:hover, .call:hover{ background:${CINZA_CLARO}; border-color:${CINZA_CLARO}; }
  .duty .status, .tcard code, .tcard .ref{ color:${CINZA_CLARO}; }
  .tcard .ref{ border-color:rgba(226,232,240,.55); }

  /* Painéis que eram creme (#FEF6E0 / #FFF3D1) com o texto marrom (#5A4408). */
  .noduty, .note, .bloco .obs{ background:${CINZA_PALIDO}; }
  .ref{ background:${CINZA_PALIDO}; }
  .noduty p, .note, .bloco .obs{ color:${CINZA_TEXTO}; }

  /* O mesmo marrom, mas escrito EM CIMA do acento — a etiqueta da rota e o
     número dos passos. */
  .rota li.on .tag, .steps li::before{ color:${CINZA_TEXTO}; }

  /* Os halos de foco eram amarelos por rgba fixo, fora da variável. */
  .busca input:focus,
  .resumo .loja input:focus,
  .eqcampo input:focus,
  .eqcolar textarea:focus,
  .editor-campo input:focus, .editor-campo select:focus, .editor-campo textarea:focus{
    box-shadow:0 0 0 4px rgba(${BRILHO},.35);
  }
  .card.alvo{ box-shadow:0 0 0 4px rgba(${BRILHO},.45); }
  .foto:hover{ box-shadow:0 0 0 3px rgba(${BRILHO},.35); }

  /* ⚠️ O AMARELO QUE SÓ APARECE NO CELULAR: o flash do toque. Fica em uma regra
     global (\`button, summary, a\`) e não em variável — no computador ninguém o
     vê, e ele piscaria amarelo a cada toque num guia que é lido na loja, no
     aparelho. */
  button, summary, a{ -webkit-tap-highlight-color:rgba(${BRILHO},.35); }

  /* ⚠️ A piscada de chegada (o guia pisca o alvo ao pular para uma ficha) é
     \`@keyframes\`, e passo de animação não se sobrescreve de fora: o jeito é
     redeclarar a animação inteira com o mesmo nome — a última vence. */
  @keyframes pisca{ 0%, 60%{ background:rgba(${BRILHO},.35); } 100%{ background:transparent; } }

  /* ⚠️ A ÚNICA EXCEÇÃO AO CINZA ÚNICO — e ela tem motivo: o realce do trecho
     encontrado na busca. Todo o resto do acento é fundo de botão ou borda, onde
     um cinza claro funciona; aqui o bloco precisa SALTAR do texto branco, e o
     cinza do acento sobre branco dá 1,5:1 — praticamente invisível. Num guia
     que existe para ACHAR um sintoma, realce que não se acha derruba a
     ferramenta. Este cinza mais forte dá 2,6:1 contra o fundo (o amarelo dava
     1,8:1) e 6,4:1 para o texto por cima. */
  .hit{ background:${CINZA_FORTE}; }

  /* O aviso do editor tinha fundo rosado, casado com a borda vermelha. */
  .editor-alerta{ background:${ROXO_CLARO}; }
</style>`;
}

/**
 * Devolve o HTML do guia com o tema do módulo aplicado.
 *
 * @param {string} html o guia como veio do backend
 * @param {{cor?: string, corEscura?: string}} [cores] o matiz da fila, em HEX.
 *        Omitido = lê do portal por `resolverCoresDoModulo()`.
 * @returns {string} o mesmo HTML com o bloco de cor no fim do `<head>`
 */
export function injetarTemaDoModulo(html, cores) {
  if (typeof html !== 'string' || !html) return html;
  const i = html.search(/<\/head\s*>/i);
  if (i < 0) return html;   // sem `</head>`: devolve intacto, nunca meio pintado
  return html.slice(0, i) + montarTemaDoModulo(cores || resolverCoresDoModulo()) + html.slice(i);
}
